import React, { useCallback, useRef } from 'react'

export type Position = {
  x: number
  y: number
}

export interface UseLongPressOptions {
  onLongPress: (position: Position, event: React.TouchEvent | React.MouseEvent) => void
  onClick?: (event: React.TouchEvent | React.MouseEvent) => void
  ms?: number
  moveThreshold?: number
}

export const useLongPress = ({
  onLongPress,
  onClick,
  ms = 500,
  moveThreshold = 1,
}: UseLongPressOptions) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const isLongPressActive = useRef(false)
  const startPos = useRef<Position>(null)

  const start = useCallback(
    (event: React.TouchEvent | React.MouseEvent) => {
      const position: Position = {
        x: 'touches' in event ? event.touches[0].clientX : event.clientX,
        y: 'touches' in event ? event.touches[0].clientY : event.clientY,
      }

      startPos.current = position
      isLongPressActive.current = false

      timerRef.current = setTimeout(() => {
        isLongPressActive.current = true
        onLongPress(position, event)
      }, ms)
    },
    [onLongPress, ms],
  )

  const move = useCallback(
    (event: React.TouchEvent | React.MouseEvent) => {
      if (!startPos.current) return

      const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX
      const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY

      const distance = Math.sqrt(
        Math.pow(clientX - startPos.current.x, 2) + Math.pow(clientY - startPos.current.y, 2),
      )

      if (distance > moveThreshold && !isLongPressActive.current) {
        if (timerRef.current) {
          clearTimeout(timerRef.current)
          timerRef.current = null
        }
      }
    },
    [moveThreshold],
  )

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    startPos.current = null
  }, [])

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isLongPressActive.current && onClick) {
        onClick(e)
      }

      isLongPressActive.current = false
    },
    [onClick],
  )

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }, [])

  return {
    onClick: handleClick,
    onContextMenu: handleContextMenu,
    onMouseDown: start,
    onMouseLeave: stop,
    onMouseMove: move,
    onMouseUp: stop,
    onTouchEnd: stop,
    onTouchMove: move,
    onTouchStart: start,
  }
}
